import * as d3 from 'd3'
import {
  SPECIAL_COLORS,
  getScenarioColor as getColorFromId,
} from '../../../stores/styles/BuildScenarioStore.styles'

class ScenarioPlotD3 {
  constructor() {
    this.permanentScales = null
  }

  /**
   * Helper function to get scenario color
   */
  getScenarioColor(scenario) {
    // Try multiple fallbacks to get the correct color
    if (scenario.color) {
      return scenario.color
    }

    // If it's a clean baseline, use the special color
    if (scenario.isCleanBaseline) {
      return SPECIAL_COLORS.CLEAN_BASELINE
    }

    // Try to get color from scenario ID using the color schema
    if (scenario.scenarioId && typeof scenario.scenarioId === 'number') {
      return getColorFromId(scenario.scenarioId)
    }

    // Final fallback to default grey
    return SPECIAL_COLORS.DEFAULT
  }

  /**
   * Prepare and combine scenario data from both outputs
   */
  prepareScenarioData(output, initialCleanOutput, scenarioNames) {
    // Combine scenario data from both outputs
    const scenarios = output?.scenarios || []
    const cleanScenarios = initialCleanOutput?.scenarios || []

    // Add clean scenarios to the main scenarios array with special identification
    const allScenarios = [
      ...scenarios,
      ...cleanScenarios.map((cleanScenario) => ({
        ...cleanScenario,
        scenarioId: `clean-${cleanScenario.scenarioId}`, // Unique ID for clean scenarios
        scenarioName: 'Baseline prediction', // Special name for clean baseline
        color: SPECIAL_COLORS.CLEAN_BASELINE, // Use ice blue for clean baseline
        isCleanBaseline: true, // Flag to identify this as baseline data
      })),
    ]

    // Collect all data points from all scenarios for unified scales
    const allDataPoints = []
    const allTimestamps = new Set()

    allScenarios.forEach((scenario) => {
      // Handle new simplified format: convert observationValues and observationTimecodes to data points
      if (scenario.observationValues && scenario.observationTimecodes) {
        // Get scenario name from store based on ID
        let scenarioName
        if (scenario.isCleanBaseline) {
          scenarioName = 'Baseline prediction'
        } else {
          const storeScenario = scenarioNames.find(
            (s) => s.id === scenario.scenarioId,
          )
          scenarioName = storeScenario
            ? storeScenario.name
            : `Scenario ${scenario.scenarioId}`
        }

        scenario.observationValues.forEach((value, index) => {
          const timestamp = scenario.observationTimecodes[index]
          if (timestamp) {
            const parsedTimestamp = new Date(timestamp)
            if (!isNaN(parsedTimestamp)) {
              allDataPoints.push({
                timestamp: timestamp,
                averageValue: value,
                parsedTimestamp,
                scenarioId: scenario.scenarioId,
                scenarioName: scenarioName,
                isCleanBaseline: scenario.isCleanBaseline || false,
              })
              allTimestamps.add(parsedTimestamp.getTime())
            }
          }
        })
      }
    })

    return {
      allScenarios,
      allDataPoints,
      allTimestamps,
    }
  }

  /**
   * Create or get permanent scales for consistent chart rendering
   */
  createScales(allDataPoints, width, height) {
    let xScale, yScale

    if (!this.permanentScales) {
      // First time drawing - create permanent scales based on all data
      xScale = d3
        .scaleTime()
        .domain(d3.extent(allDataPoints, (d) => d.parsedTimestamp))
        .range([0, width])

      // Calculate Y domain with 35% padding on top and bottom
      const [yMin, yMax] = d3.extent(allDataPoints, (d) => d.averageValue)
      const yRange = yMax - yMin
      const paddedYMin = yMin - yRange * 0.35
      const paddedYMax = yMax + yRange * 0.35

      yScale = d3
        .scaleLinear()
        .domain([paddedYMin, paddedYMax])
        .nice()
        .range([height, 0])

      // Store the permanent scales
      this.permanentScales = {
        xDomain: xScale.domain(),
        yDomain: yScale.domain(),
      }
    } else {
      // Use the permanent scales
      xScale = d3
        .scaleTime()
        .domain(this.permanentScales.xDomain)
        .range([0, width])

      yScale = d3
        .scaleLinear()
        .domain(this.permanentScales.yDomain)
        .range([height, 0])
    }

    return { xScale, yScale }
  }

  /**
   * Draw chart axes
   */
  drawAxes(g, xScale, yScale, width, height, margin, allTimestamps) {
    const formatTime = d3.timeFormat('%m-%Y')

    // Add X axis (without axis line and ticks)
    const xAxis = g
      .append('g')
      .attr('transform', `translate(0, ${height - 34})`) // Removing the height of the text itself since translate will start from the bottom
      .call(
        d3
          .axisBottom(xScale)
          .tickFormat(formatTime)
          .tickSize(0) // Remove tick marks
          .ticks(Math.min(10, Array.from(allTimestamps).length)),
      )

    // Style x-axis text and remove axis line
    xAxis.selectAll('text').style('text-anchor', 'middle').style('fill', '#999') // Grey axis text

    // Remove the x-axis line (domain line)
    xAxis.select('.domain').remove()

    // Add Y axis (without axis line and ticks)
    const yAxis = g.append('g').call(d3.axisLeft(yScale).tickSize(0)) // Remove tick marks

    // Style y-axis text and remove axis line
    yAxis.selectAll('text').style('fill', '#999') // Grey axis text

    // Remove the y-axis line (domain line)
    yAxis.select('.domain').remove()
  }

  /**
   * Draw scenario lines and points
   */
  drawScenarioLines(
    g,
    allScenarios,
    allDataPoints,
    xScale,
    yScale,
    scenarioNames,
  ) {
    // Sort scenarios so clean baseline (Model prediction) is drawn first (appears behind)
    const sortedScenariosForDrawing = [...allScenarios].sort((a, b) => {
      // Clean baseline scenarios should be drawn first (lower z-index/behind)
      if (a.isCleanBaseline && !b.isCleanBaseline) return -1
      if (!a.isCleanBaseline && b.isCleanBaseline) return 1
      return 0 // Keep original order for scenarios of the same type
    })

    // Draw lines and points for each scenario
    let nonBaselineScenarioCount = 1 // Counter for non-baseline scenarios only

    sortedScenariosForDrawing.forEach((scenario, index) => {
      const scenarioData = allDataPoints.filter(
        (d) => d.scenarioId === scenario.scenarioId,
      )

      if (scenarioData.length === 0) return

      // Calculate Y-axis shift: clean baseline = 0, then 0.5px increments for each non-baseline scenario
      let yShift = 0
      if (!scenario.isCleanBaseline) {
        yShift = nonBaselineScenarioCount * -2.5
        nonBaselineScenarioCount++ // Increment counter only for non-baseline scenarios
      }

      // Create line generator with Y-axis shift for this scenario
      const shiftedLine = d3
        .line()
        .x((d) => xScale(d.parsedTimestamp))
        .y((d) => yScale(d.averageValue) - yShift) // Apply Y-shift here
        .curve(d3.curveMonotoneX)

      // Get scenario color from the scenario's color property
      const scenarioColor = this.getScenarioColor(scenario)

      // Add the line
      g.append('path')
        .datum(scenarioData)
        .attr('fill', 'none')
        .attr('stroke', scenarioColor)
        .attr('stroke-width', scenario.isCleanBaseline ? 3 : 3) // 3x thicker: baseline 9px, regular 6px
        .attr('d', shiftedLine) // Use the shifted line generator
        .style('opacity', scenario.isCleanBaseline ? 1.0 : 0.8) // Full opacity for baseline

      // Add data points with Y-axis shift
      g.selectAll(
        `.dot-scenario-${String(scenario.scenarioId).replace(/[^a-zA-Z0-9]/g, '-')}`,
      ) // Clean ID for CSS class
        .data(scenarioData)
        .enter()
        .append('circle')
        .attr(
          'class',
          `dot-scenario-${String(scenario.scenarioId).replace(/[^a-zA-Z0-9]/g, '-')}`,
        )
        .attr('cx', (d) => xScale(d.parsedTimestamp))
        .attr('cy', (d) => yScale(d.averageValue) - yShift) // Apply Y-shift to data points too
        .attr('r', scenario.isCleanBaseline ? 4.2 : 4.2) // 30% smaller: 6 * 0.7 = 4.2
        .attr('fill', scenarioColor)
        .attr('stroke', '#fff')
        .attr('stroke-width', 0.5)
        .style('opacity', 1)
    })
  }

  /**
   * Get legend data for CSS rendering
   */
  getLegendData(allScenarios, scenarioNames) {
    // Add legend - sort so clean baseline appears first
    const sortedScenarios = allScenarios.sort((a, b) => {
      if (a.isCleanBaseline && !b.isCleanBaseline) return -1
      if (!a.isCleanBaseline && b.isCleanBaseline) return 1
      return 0
    })

    return sortedScenarios.map((scenario) => {
      // Get scenario name from store based on ID (same logic as in data points)
      let displayName
      if (scenario.isCleanBaseline) {
        displayName = 'Baseline prediction'
      } else {
        const storeScenario = scenarioNames.find(
          (s) => s.id === scenario.scenarioId,
        )
        displayName = storeScenario
          ? storeScenario.name
          : `Scenario ${scenario.scenarioId}`
      }

      return {
        id: scenario.scenarioId,
        name: displayName,
        color: this.getScenarioColor(scenario),
        isBaseline: scenario.isCleanBaseline,
      }
    })
  }

  /**
   * Main chart drawing function
   */
  drawXGBoostTimeSeriesChart(
    svgRef,
    output,
    initialCleanOutput,
    scenarioNames,
    containerWidth = 1000,
    containerHeight = 450,
  ) {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const margin = { top: 40, right: 20, bottom: 60, left: 42 } // Reduced left margin by 30% to bring chart closer to left edge
    const width = containerWidth - margin.left - margin.right
    const height = containerHeight - margin.top - margin.bottom

    svg.attr('width', containerWidth).attr('height', containerHeight)

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Prepare data
    const { allScenarios, allDataPoints, allTimestamps } =
      this.prepareScenarioData(output, initialCleanOutput, scenarioNames)

    if (allScenarios.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('fill', '#666')
        .text('No scenario data available')
      return []
    }

    if (allDataPoints.length === 0) {
      g.append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '14px')
        .style('fill', '#666')
        .text('No valid data points available')
      return []
    }

    // Create scales
    const { xScale, yScale } = this.createScales(allDataPoints, width, height)

    // Draw chart components
    this.drawAxes(g, xScale, yScale, width, height, margin, allTimestamps)
    this.drawScenarioLines(
      g,
      allScenarios,
      allDataPoints,
      xScale,
      yScale,
      scenarioNames,
    )

    // Return legend data for CSS rendering
    return this.getLegendData(allScenarios, scenarioNames)
  }

  /**
   * Update loading indicator
   */
  updateLoadingIndicator(
    svgRef,
    isLoading,
    containerWidth = 1000,
    containerHeight = 450,
  ) {
    const svg = d3.select(svgRef.current)

    // Remove existing loading indicator
    svg.select('.loading-indicator').remove()

    if (isLoading) {
      // Add loading indicator in center of graph (dynamic positioning)
      const loadingGroup = svg
        .append('g')
        .attr('class', 'loading-indicator')
        .attr(
          'transform',
          `translate(${containerWidth / 2}, ${containerHeight / 2})`,
        )

      // Create spinning circle
      const spinner = loadingGroup
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 12)
        .attr('fill', 'none')
        .attr('stroke', '#2e7d32')
        .attr('stroke-width', 3)
        .attr('stroke-dasharray', '6 6')
        .attr('opacity', 0.8)

      // Add spinning animation
      spinner
        .transition()
        .duration(1000)
        .ease(d3.easeLinear)
        .attrTween('transform', () => {
          return (t) => `rotate(${t * 360})`
        })
        .on('end', function repeat() {
          if (svg.select('.loading-indicator').node()) {
            d3.select(this)
              .transition()
              .duration(1000)
              .ease(d3.easeLinear)
              .attrTween('transform', () => {
                return (t) => `rotate(${t * 360})`
              })
              .on('end', repeat)
          }
        })

      // Add loading text
      loadingGroup
        .append('text')
        .attr('x', 0)
        .attr('y', 30)
        .attr('text-anchor', 'middle')
        .style('font-size', '15px')
        .style('fill', '#666')
        .text('Loading...')
    }
  }

  /**
   * Reset permanent scales (useful when data changes significantly)
   */
  resetScales() {
    this.permanentScales = null
  }
}

// Export a singleton instance
export const scenarioPlotD3 = new ScenarioPlotD3()

// Also export the class for creating new instances if needed
export { ScenarioPlotD3 }
